import styles from 'components/DSYS/editor/plugins/Comment.module.css';

export type Thread = {
  id: string;
  is_resolved?: boolean | null;
  comments?: Array<{is_deleted?: boolean | null}> | null;
};

export const COMMENT_THREAD_HOVER_EVENT = 'comment-thread-hover';
const COMMENT_HOVERED_ATTRIBUTE = 'data-comment-hovered';

const rootToThreads = new WeakMap<HTMLElement, readonly Thread[]>();

export const setThreadsDataToEditorRoot = (
  root: HTMLElement,
  threads: readonly Thread[] | undefined
): void => {
  if (threads === undefined) {
    rootToThreads.delete(root);
    return;
  }
  rootToThreads.set(root, threads);
};

export const unregisterThreadsDataFromEditorRoot = (root: HTMLElement): void => {
  rootToThreads.delete(root);
};

/**
 * Returns all DOM elements that contain the provided thread ID
 */
export const getCommentElementsForThread = (threadId: string): HTMLElement[] => {
  const safeThreadId =
    typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
      ? CSS.escape(threadId)
      : threadId;

  return Array.from(
    document.querySelectorAll<HTMLElement>(`span[data-highlight-id~="${safeThreadId}"]`)
  ).filter((element) => element.classList.contains(styles.comment));
};

const getHighlightElementsForThread = (threadId: string): HTMLElement[] => {
  const safeThreadId =
    typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
      ? CSS.escape(threadId)
      : threadId;

  return Array.from(
    document.querySelectorAll<HTMLElement>(`[data-highlight-id~="${safeThreadId}"]`)
  );
};

export const clearCommentThreadHoverHighlights = () => {
  document.querySelectorAll<HTMLElement>('[data-highlight-id]').forEach((element) => {
    element.classList.remove(styles.highlighted);
    element.removeAttribute(COMMENT_HOVERED_ATTRIBUTE);
  });
};

export const setCommentThreadHoverHighlight = (threadId: string, hovered: boolean) => {
  getHighlightElementsForThread(threadId).forEach((element) => {
    element.toggleAttribute(COMMENT_HOVERED_ATTRIBUTE, hovered);
  });

  getCommentElementsForThread(threadId).forEach((element) => {
    if (hovered && element.classList.contains(styles.focused)) {
      return;
    }

    element.classList.toggle(styles.highlighted, hovered);
  });
};

export const emitCommentThreadHover = (threadId: string | undefined) => {
  window.dispatchEvent(
    new CustomEvent<{threadId: string | undefined}>(COMMENT_THREAD_HOVER_EVENT, {
      detail: {threadId},
    })
  );
};

/**
 * Calculate the total text length for all DOM elements associated with a given thread ID
 */
export const getHighlightedTextLengthForThread = (threadId: string): number => {
  const textNodes = new Set<Text>();

  getCommentElementsForThread(threadId).forEach((element) => {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);

    let currentNode = walker.nextNode();
    while (currentNode) {
      textNodes.add(currentNode as Text);
      currentNode = walker.nextNode();
    }
  });

  let totalLength = 0;
  textNodes.forEach((textNode) => {
    totalLength += textNode.textContent?.length ?? 0;
  });

  return totalLength;
};

/**
 * Returns threads registered on the nearest ancestor editor root of provided element
 */
export const getThreadsForElement = (element: HTMLElement): readonly Thread[] => {
  let current: HTMLElement | null = element;
  while (current) {
    if (rootToThreads.has(current)) {
      return rootToThreads.get(current) ?? [];
    }
    current = current.parentElement;
  }
  return [];
};

/**
 * When multiple comment threads overlap on the same DOM element, choose the
 * thread with the smallest total highlighted text range. This makes interactions
 * prefer the most specific comment. Only considers active (unresolved, non-deleted) threads.
 * If threads list provided is empty, method will consider all thread IDs on the element as active.
 */
export const getMostSpecificActiveHighlightId = (
  element: HTMLElement,
  threads: readonly Thread[]
): string | null => {
  const ids = element.getAttribute('data-highlight-id')?.split(/\s+/).filter(Boolean) ?? [];

  if (ids.length === 0) {
    return null;
  }

  // Filter to only active threads (ie. unresolved and have active comments)
  const activeIds = threads
    ? ids.filter((id) => {
        const thread = threads.find((t) => t.id === id);
        if (!thread) return false;
        return (
          !thread.is_resolved && thread.comments?.some((comment) => !comment.is_deleted) === true
        );
      })
    : ids;

  if (activeIds.length === 0) {
    return null;
  }

  if (activeIds.length === 1) {
    return activeIds[0];
  }

  let bestId = activeIds[0];
  let bestLength = Number.POSITIVE_INFINITY;

  for (const id of activeIds) {
    const length = getHighlightedTextLengthForThread(id);

    if (length < bestLength) {
      bestId = id;
      bestLength = length;
    }
  }

  return bestId;
};
